// AdminReferralsPage — programa de REFERIDOS de plataforma (super_admin, F1 12-oct-2026).
// Quién trajo a qué negocio que le paga a Lealify, con qué esquema, cuánto ganó por mes,
// cuánto se le ha pagado y cuánto se le debe. Carlos registra aquí los pagos al referidor.
// Plan: forobeta-ops/docs/lealify-referidos-plataforma-plan-20261012.md
// 🚨 El % es el monto TOTAL a entregar (impuestos del referidor incluidos): no se suma nada encima.
// 4-nov-2026 (Carlos): cada comisión dice si YA está anotada en el grupo de WhatsApp «Mandado» (el libro
// de lo que se le debe a la referidora) o es NUEVA, y cada negocio dice si su comisión está COMPLETA o
// SIGUE GENERANDO (cálculo en backend: lib/platformReferrals.js vigenciaOf).

import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import NumInput from '../../../components/ui/NumInput';
import { useAuth } from '../../../context/AuthContext';
import Layout from '../../../components/layout/Layout';
import { Handshake, RefreshCw, Plus, X, LifeBuoy, ChevronLeft, CheckCircle2, CircleDot, AlertTriangle } from 'lucide-react';
import {
  adminReferralsApi, Referrer, ReferrerDetail, TenantOption, ReferralKind, SchemeInput,
  KIND_LABEL, schemeText, EarningRow, Vigencia, VigenciaState, GrupoResumen,
} from '../../../api/adminReferralsApi';

const money = (n: number | string | null | undefined, cur = 'MXN') =>
  `$${Number(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ${cur}`;
const fmtDia = (s: string | null | undefined) =>
  s ? new Date(s.length === 10 ? `${s}T12:00:00` : s).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const fmtMes = (p: string) => {
  const [y, m] = p.split('-').map(Number);
  return new Date(y, m - 1, 15).toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
};
const hoy = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));

const input = 'w-full px-2.5 py-2 text-sm rounded-md border border-line bg-surface text-content';
const btn = 'inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md border border-line bg-surface text-content hover:bg-surface-2 disabled:opacity-50';
const btnPrimary = 'inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md bg-accent text-white hover:opacity-90 disabled:opacity-50';
const card = 'bg-surface border border-line rounded-xl p-4';

const STATUS_REF: Record<string, { label: string; chip: string }> = {
  active: { label: 'Vigente', chip: 'bg-green-500/15 text-green-600' },
  ended: { label: 'Terminado', chip: 'bg-gray-500/15 text-content-3' },
  void: { label: 'Quitado', chip: 'bg-red-500/15 text-red-600' },
};
const STATUS_EARN: Record<string, { label: string; chip: string }> = {
  pending: { label: 'Pendiente', chip: 'bg-amber-500/15 text-amber-600' },
  paid: { label: 'Pagada', chip: 'bg-green-500/15 text-green-600' },
  applied: { label: 'Meses aplicados', chip: 'bg-sky-500/15 text-sky-600' },
  void: { label: 'Anulada', chip: 'bg-red-500/15 text-red-600' },
};

// Estado de la comisión de un negocio dentro de la vigencia de su esquema.
const VIG_CHIP: Record<VigenciaState, string> = {
  sigue: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  completa: 'bg-gray-500/15 text-content-2',
  ended: 'bg-gray-500/15 text-content-3',
  void: 'bg-red-500/15 text-red-600',
  aun_no_paga: 'bg-sky-500/15 text-sky-700 dark:text-sky-300',
  sin_renovacion: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
};

// ── Tooltip accesible (hover, foco con teclado o toque en móvil). Va en un portal con posición fija
// para que no lo recorte el overflow-x de las tablas.
const Tip: React.FC<{ label: string; content: React.ReactNode; children: React.ReactNode; className?: string }> = ({ label, content, children, className = '' }) => {
  const id = useId();
  const ref = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<React.CSSProperties | null>(null);
  const show = () => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const w = Math.min(288, window.innerWidth - 16);
    const left = Math.max(8, Math.min(r.left, window.innerWidth - w - 8));
    setPos(window.innerHeight - r.bottom < 220 && r.top > 220
      ? { position: 'fixed', bottom: window.innerHeight - r.top + 6, left, width: w }
      : { position: 'fixed', top: r.bottom + 6, left, width: w });
  };
  const hide = () => setPos(null);
  useEffect(() => {
    if (!pos) return;
    window.addEventListener('scroll', hide, true); window.addEventListener('resize', hide);
    return () => { window.removeEventListener('scroll', hide, true); window.removeEventListener('resize', hide); };
  }, [pos]);
  return (
    <>
      <button ref={ref} type="button" aria-label={label} aria-describedby={pos ? id : undefined}
        onMouseEnter={show} onMouseLeave={hide} onFocus={show} onBlur={hide} onClick={show}
        onKeyDown={(e) => { if (e.key === 'Escape') hide(); }} className={`cursor-help text-left ${className}`}>{children}</button>
      {pos && createPortal(
        <div role="tooltip" id={id} style={pos} className="z-[100] rounded-md bg-content text-surface text-xs leading-snug p-2.5 shadow-lg space-y-1 pointer-events-none">{content}</div>,
        document.body)}
    </>
  );
};

// ── «¿Ya está en el grupo Mandado?» ──────────────────────────────────────────
/** Comisión con dinero que cuenta para el grupo (las de meses gratis y las anuladas no se anotan ahí). */
const cuentaGrupo = (e: EarningRow) => Number(e.amount) > 0 && e.status !== 'void' && e.status !== 'applied';
/** Falta anotar = pendiente de pagarle y sin marca (mismo criterio que el backend). */
const faltaAnotar = (e: EarningRow) => e.status === 'pending' && Number(e.amount) > 0 && !e.grupo_anotado_at;
const deQue = (e: EarningRow) => `${fmtMes(e.period)}${e.payment_id ? ` · pago #${e.payment_id}` : ''} · generada ${fmtDia(e.created_at)}`;
/** Abono parcial: «parcial: $2,659 de $3,000» (null si no tiene abono o ya está pagada completa). */
const parcialTxt = (e: EarningRow) => (e.status === 'pending' && Number(e.paid_amount) > 0
  ? `parcial: ${money(e.paid_amount, e.currency)} de ${money(e.amount, e.currency)} (faltan ${money(Number(e.amount) - Number(e.paid_amount), e.currency)})` : null);
const sumaGrupo = (g: GrupoResumen | undefined) => Object.entries(g || {}).filter(([, v]) => v.amount > 0);

const GrupoTipBody: React.FC<{ e: EarningRow; otras?: EarningRow[] }> = ({ e, otras = [] }) => {
  const faltan = otras.filter((x) => x.id !== e.id && faltaAnotar(x));
  return (
    <>
      <p className="font-semibold">{e.grupo_anotado_at ? `En el grupo desde el ${fmtDia(e.grupo_anotado_at)}` : 'Nueva: todavía no está anotada en el grupo Mandado'}</p>
      <p>{money(e.amount, e.currency)} · {e.tenant_name} · {parcialTxt(e) ? `pagada ${parcialTxt(e)}` : e.status === 'paid' ? 'ya pagada' : 'pendiente de pagarle'}</p>
      <p className="opacity-80">{deQue(e)}</p>
      {e.grupo_nota && <p className="opacity-80">Nota: {e.grupo_nota}</p>}
      {faltan.length > 0
        ? <p className="pt-1 border-t border-surface/30">Falta anotar de este negocio: {faltan.map((x) => `${money(x.amount, x.currency)} de ${fmtMes(x.period)} (generada ${fmtDia(x.created_at)})`).join(' · ')}</p>
        : otras.length > 1 && <p className="pt-1 border-t border-surface/30 opacity-80">Este negocio no tiene más comisiones por anotar.</p>}
    </>
  );
};

const GrupoChip: React.FC<{ e: EarningRow; otras?: EarningRow[] }> = ({ e, otras }) => (
  <Tip label={e.grupo_anotado_at ? 'Ya está en el grupo: ver detalle' : 'Falta anotar en el grupo: ver detalle'} content={<GrupoTipBody e={e} otras={otras} />}
    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs whitespace-nowrap ${e.grupo_anotado_at
      ? 'bg-green-500/15 text-green-700 dark:text-green-300'
      : e.status === 'pending' ? 'bg-amber-500/20 text-amber-700 dark:text-amber-300 font-medium' : 'bg-gray-500/15 text-content-3'}`}>
    {e.grupo_anotado_at
      ? <><CheckCircle2 className="h-3 w-3" aria-hidden /> En el grupo</>
      : e.status === 'pending' ? <><CircleDot className="h-3 w-3" aria-hidden /> Nueva — falta anotar</> : <>Sin marca</>}
  </Tip>
);

const VigenciaChip: React.FC<{ v: Vigencia | undefined }> = ({ v }) => {
  if (!v) return null;
  const extra = v.state === 'sigue' && v.next_payment_at
    ? ` · próximo pago ≈ ${fmtDia(v.next_payment_at)}${v.next_estimate != null ? ` (+${money(v.next_estimate)})` : ''}` : '';
  return (
    <Tip label={`${v.label}: cómo se calcula`} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${VIG_CHIP[v.state]}`}
      content={<>
        <p className="font-semibold">{v.label}{extra}</p>
        {v.lines.filter((l) => l !== v.warning).map((l) => <p key={l}>{l}</p>)}
        {v.warning && <p className="pt-1 border-t border-surface/30">{v.warning}</p>}
      </>}>
      {v.warning && <AlertTriangle className="h-3 w-3" aria-hidden />}{v.label}{extra}
    </Tip>
  );
};

// ── Selector de esquema (reutilizado en alta y en cambio de esquema) ─────────
const SchemeFields: React.FC<{ value: SchemeInput; onChange: (s: SchemeInput) => void }> = ({ value, onChange }) => (
  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
    <select className={input} value={value.kind}
      onChange={(e) => {
        const kind = e.target.value as ReferralKind;
        onChange(kind === 'free_months' ? { kind, free_months: 2 } : { kind, rate: kind === 'first_year_pct' ? 50 : 20 });
      }}>
      <option value="first_year_pct">% el primer año</option>
      <option value="recurring_pct">% recurrente (para siempre)</option>
      <option value="free_months">Meses gratis</option>
    </select>
    {value.kind === 'free_months' ? (
      <NumInput className={input} min={1} max={24} value={value.free_months}
        onValue={(n) => onChange({ ...value, free_months: n })} placeholder="Meses por referido" />
    ) : (
      <NumInput className={input} min={1} max={100} step="0.5" value={value.rate}
        onValue={(n) => onChange({ ...value, rate: n })} placeholder="%" />
    )}
    <input className={input} type="date" value={value.effective_from || hoy()}
      onChange={(e) => onChange({ ...value, effective_from: e.target.value })} title="Aplica a referidos atribuidos desde esta fecha" />
  </div>
);

// ── Alta de referidor ────────────────────────────────────────────────────────
const NewReferrerForm: React.FC<{ tenants: TenantOption[]; onDone: () => void; onCancel: () => void }> = ({ tenants, onDone, onCancel }) => {
  const [f, setF] = useState({ name: '', code: '', phone: '', own_tenant_id: '', notes: '' });
  const [scheme, setScheme] = useState<SchemeInput>({ kind: 'first_year_pct', rate: 50, effective_from: hoy() });
  const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null);
  const save = async () => {
    setBusy(true); setErr(null);
    try {
      await adminReferralsApi.createReferrer({ name: f.name, code: f.code, phone: f.phone || undefined,
        own_tenant_id: f.own_tenant_id ? Number(f.own_tenant_id) : null, notes: f.notes || undefined,
        scheme, effective_from: scheme.effective_from });
      onDone();
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className={`${card} space-y-3`}>
      <div className="flex items-center justify-between"><h2 className="font-semibold text-content">Nuevo referidor</h2>
        <button className="text-content-3" onClick={onCancel} aria-label="Cerrar"><X className="h-4 w-4" /></button></div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <input className={input} placeholder="Nombre" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <input className={input} placeholder="Código (HECTOR)" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} />
        <input className={input} placeholder="WhatsApp (opcional)" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
      </div>
      <select className={input} value={f.own_tenant_id} onChange={(e) => setF({ ...f, own_tenant_id: e.target.value })}>
        <option value="">Su negocio (para meses gratis) — ninguno</option>
        {tenants.map((t) => <option key={t.id} value={t.id}>{t.name} (t{t.id})</option>)}
      </select>
      <SchemeFields value={scheme} onChange={setScheme} />
      <input className={input} placeholder="Notas (opcional)" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
      {err && <p className="text-sm text-red-600">{err}</p>}
      <button className={btnPrimary} disabled={busy || !f.name || !f.code} onClick={save}>Guardar</button>
    </div>
  );
};

// ── Detalle de un referidor ──────────────────────────────────────────────────
const ReferrerDetailView: React.FC<{ id: number; tenants: TenantOption[]; onBack: () => void; onChanged: () => void }> = ({ id, tenants, onBack, onChanged }) => {
  const [d, setD] = useState<ReferrerDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [newScheme, setNewScheme] = useState<SchemeInput | null>(null);
  const [attr, setAttr] = useState({ tenant_id: '', attributed_at: hoy() });
  const [pay, setPay] = useState({ amount: '', paid_at: hoy(), method: 'transferencia', notes: '' });
  const [man, setMan] = useState({ referral_id: '', amount: '', months: '', apply: true, description: '', date: hoy() });

  const load = useCallback(async () => {
    try { setD(await adminReferralsApi.detail(id)); setErr(null); } catch (e) { setErr(errMsg(e)); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true); setErr(null);
    try { await fn(); await load(); onChanged(); } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };

  const totals = useMemo(() => {
    const by: Record<string, { earned: number; paid: number; pending: number }> = {};
    for (const e of d?.earnings || []) {
      if (e.status === 'void' || e.status === 'applied') continue;
      const t = (by[e.currency] ||= { earned: 0, paid: 0, pending: 0 });
      t.earned += Number(e.amount); t.paid += Number(e.paid_amount);
      if (e.status === 'pending') t.pending += Number(e.amount) - Number(e.paid_amount);
    }
    return by;
  }, [d]);
  const monthsApplied = (d?.earnings || []).filter((e) => e.status === 'applied').reduce((s, e) => s + (e.months_granted || 0), 0);
  const delNegocio = (referralId: number) => (d?.earnings || []).filter((e) => e.referral_id === referralId && cuentaGrupo(e));
  const anotar = (e: EarningRow) => {
    const nota = window.prompt(`Marcar ${money(e.amount, e.currency)} de ${e.tenant_name} (${fmtMes(e.period)}) como YA anotada hoy en el grupo Mandado.\nNota (opcional):`, '');
    if (nota === null) return;
    run(() => adminReferralsApi.markGrupo(e.id, { anotado: true, nota: nota.trim() || null, fecha: hoy() }));
  };
  const desmarcar = (e: EarningRow) => {
    if (window.confirm(`¿Quitar la marca «en el grupo» de ${money(e.amount, e.currency)} de ${e.tenant_name}? Volverá a salir como nueva.`)) {
      run(() => adminReferralsApi.markGrupo(e.id, { anotado: false }));
    }
  };
  const faltaGrupo = sumaGrupo(d?.grupo);
  const faltaGrupoN = faltaGrupo.reduce((s, [, v]) => s + v.n, 0);
  const free = tenants.filter((t) => !t.referral_id);

  if (!d) return <div className={card}>{err ? <p className="text-sm text-red-600">{err}</p> : <p className="text-sm text-content-3">Cargando…</p>}</div>;
  const r = d.referrer;
  const current = d.schemes.find((s) => new Date(`${String(s.effective_from).slice(0, 10)}T12:00:00`) <= new Date()) || d.schemes[0];

  return (
    <div className="space-y-4">
      <button className={btn} onClick={onBack}><ChevronLeft className="h-4 w-4" /> Todos los referidores</button>
      {err && <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 rounded-md p-3 text-sm text-red-700 dark:text-red-300">{err}</div>}

      <div className={`${card} space-y-3`}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-lg font-bold text-content">{r.name} <span className="text-sm font-medium text-content-3">· {r.code}</span></h2>
            <p className="text-sm text-content-2">{schemeText(current)}{r.own_tenant_name ? ` · su negocio: ${r.own_tenant_name}` : ''}</p>
            {/* F3 (12-oct-2026): enlace para que el referidor lo comparta; abre WhatsApp a Carlos o el alta y atribuye solo. */}
            <p className="text-sm text-content-2 mt-1 flex items-center gap-2 flex-wrap">
              Su enlace: <a className="font-medium text-blue-700 dark:text-blue-300 underline" href={`https://lealify.com/r/${r.code}`} target="_blank" rel="noreferrer">lealify.com/r/{r.code}</a>
              <button className={btn} onClick={() => { navigator.clipboard?.writeText(`https://lealify.com/r/${r.code}`).catch(() => { /* sin permiso de portapapeles */ }); }}>Copiar</button>
            </p>
          </div>
          <button className={btn} disabled={busy} onClick={() => run(() => adminReferralsApi.updateReferrer(r.id, { active: !r.active }))}>
            {r.active ? 'Desactivar' : 'Activar'}
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {Object.entries(totals).length === 0 && monthsApplied === 0 && <p className="text-sm text-content-3">Todavía no ha generado nada.</p>}
          {Object.entries(totals).map(([cur, t]) => (
            <React.Fragment key={cur}>
              <div className="rounded-lg bg-amber-500/10 p-3"><p className="text-xs text-content-3">Pendiente por pagarle</p><p className="text-2xl font-bold text-content tabular-nums">{money(t.pending, cur)}</p></div>
              <div className="rounded-lg bg-surface-2 p-3"><p className="text-xs text-content-3">Ya pagado</p><p className="text-xl font-semibold text-content tabular-nums">{money(t.paid, cur)}</p></div>
              <div className="rounded-lg bg-surface-2 p-3"><p className="text-xs text-content-3">Total ganado</p><p className="text-xl font-semibold text-content tabular-nums">{money(t.earned, cur)}</p></div>
            </React.Fragment>
          ))}
          {monthsApplied > 0 && <div className="rounded-lg bg-sky-500/10 p-3"><p className="text-xs text-content-3">Meses gratis ya aplicados</p><p className="text-2xl font-bold text-content">{monthsApplied}</p></div>}
        </div>
        {faltaGrupo.length > 0 && (
          <div role="status" className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-500/10 p-3 text-sm text-amber-800 dark:text-amber-200">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" aria-hidden />
            <p>Falta anotar en el grupo Mandado: <b className="tabular-nums">{faltaGrupo.map(([c, v]) => money(v.amount, c)).join(' · ')}</b>
              {' '}({faltaGrupoN} {faltaGrupoN === 1 ? 'comisión nueva' : 'comisiones nuevas'}). Salen como «Nueva» abajo: al anotarlas en el grupo, márcalas.</p>
          </div>
        )}
        <p className="text-xs text-content-3">El porcentaje es el monto total a entregar: incluye IVA o retenciones del referidor.</p>
      </div>

      {/* Esquema */}
      <div className={`${card} space-y-2`}>
        <div className="flex items-center justify-between"><h3 className="font-semibold text-content">Esquema</h3>
          {!newScheme && <button className={btn} onClick={() => setNewScheme({ kind: 'recurring_pct', rate: 20, effective_from: hoy() })}>Cambiar esquema</button>}</div>
        {newScheme && (
          <div className="space-y-2">
            <SchemeFields value={newScheme} onChange={setNewScheme} />
            <p className="text-xs text-content-3">Aplica solo a los negocios que se le atribuyan desde esa fecha. Los anteriores siguen con su esquema.</p>
            <div className="flex gap-2">
              <button className={btnPrimary} disabled={busy} onClick={() => run(async () => { await adminReferralsApi.addScheme(r.id, newScheme); setNewScheme(null); })}>Guardar</button>
              <button className={btn} onClick={() => setNewScheme(null)}>Cancelar</button>
            </div>
          </div>
        )}
        <ul className="text-sm text-content-2 space-y-0.5">
          {d.schemes.map((s) => <li key={s.id}>Desde {fmtDia(String(s.effective_from).slice(0, 10))}: {schemeText(s)}</li>)}
        </ul>
      </div>

      {/* Referidos */}
      <div className={`${card} space-y-3`}>
        <h3 className="font-semibold text-content">Negocios que trajo ({d.referrals.filter((x) => x.status !== 'void').length})</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-content-3 border-b border-line">
              <th className="py-1.5 pr-3">Negocio</th><th className="pr-3">Desde</th><th className="pr-3">Esquema</th>
              <th className="pr-3">Su plan</th><th className="pr-3">Generó</th><th className="pr-3">En el grupo</th><th className="pr-3">Estado</th><th />
            </tr></thead>
            <tbody>
              {d.referrals.map((x) => (
                <tr key={x.id} className="border-b border-line/60">
                  <td className="py-1.5 pr-3 text-content">{x.tenant_name}</td>
                  <td className="pr-3 whitespace-nowrap">{fmtDia(String(x.attributed_at).slice(0, 10))}</td>
                  <td className="pr-3">{schemeText(x)}</td>
                  <td className="pr-3 whitespace-nowrap">{x.is_trial ? 'En prueba' : x.sub_status === 'active' ? 'Pagando' : (x.sub_status || '—')}{x.expires_at ? ` · vence ${fmtDia(x.expires_at)}` : ''}</td>
                  <td className="pr-3 whitespace-nowrap tabular-nums">{x.kind === 'free_months' ? `${Number(x.months)} meses` : money(x.earned)}</td>
                  <td className="pr-3 py-1.5">
                    {delNegocio(x.id).length === 0 ? <span className="text-xs text-content-3">—</span> : (
                      <div className="flex flex-col items-start gap-1">
                        {delNegocio(x.id).map((e) => (
                          <div key={e.id} className="flex items-center gap-1.5 whitespace-nowrap">
                            <span className="text-xs tabular-nums text-content-2 w-28">{money(e.amount, e.currency)} <span className="text-content-3">{fmtMes(e.period).slice(0, 3)}</span></span>
                            {parcialTxt(e) && <span className="text-xs text-amber-700 dark:text-amber-300">{parcialTxt(e)?.split(' (')[0]}</span>}
                            <GrupoChip e={e} otras={delNegocio(x.id)} />
                            {faltaAnotar(e) && <button className="text-xs text-accent hover:underline" disabled={busy} onClick={() => anotar(e)}>Marcar anotada</button>}
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="pr-3 py-1.5">
                    <div className="flex flex-col items-start gap-1">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_REF[x.status].chip}`}>{STATUS_REF[x.status].label}</span>
                      <VigenciaChip v={x.vigencia} />
                    </div>
                  </td>
                  <td>{x.status !== 'void' && (
                    <button className="text-xs text-red-600 hover:underline" disabled={busy}
                      onClick={() => window.confirm(`¿Quitar ${x.tenant_name} de ${r.name}? Lo pendiente sin pagar se anula.`) && run(() => adminReferralsApi.unattribute(x.id))}>Quitar</button>
                  )}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-2">
          <select className={input} value={attr.tenant_id} onChange={(e) => setAttr({ ...attr, tenant_id: e.target.value })}>
            <option value="">Atribuirle un negocio…</option>
            {free.map((t) => <option key={t.id} value={t.id}>{t.name} (t{t.id})</option>)}
          </select>
          <input className={input} type="date" value={attr.attributed_at} onChange={(e) => setAttr({ ...attr, attributed_at: e.target.value })} title="Fecha de atribución: define qué esquema se le copia" />
          <button className={btnPrimary} disabled={busy || !attr.tenant_id}
            onClick={() => run(async () => { await adminReferralsApi.attribute({ referrer_id: r.id, tenant_id: Number(attr.tenant_id), attributed_at: attr.attributed_at }); setAttr({ tenant_id: '', attributed_at: hoy() }); })}>
            <Plus className="h-4 w-4" /> Atribuir</button>
        </div>
      </div>

      {/* Por mes */}
      <div className={`${card} space-y-2`}>
        <h3 className="font-semibold text-content">Ganado por mes</h3>
        {d.monthly.length === 0 ? <p className="text-sm text-content-3">Sin movimientos.</p> : (
          <table className="w-full text-sm"><tbody>
            {d.monthly.map((m) => (
              <tr key={`${m.period}-${m.currency}`} className="border-b border-line/60">
                <td className="py-1.5 pr-3 capitalize text-content">{fmtMes(m.period)}</td>
                <td className="pr-3 text-content-3">{m.n} {m.n === 1 ? 'pago' : 'pagos'}</td>
                <td className="text-right tabular-nums text-content">{Number(m.amount) > 0 ? money(m.amount, m.currency) : ''}{Number(m.months) > 0 ? ` ${Number(m.months)} meses` : ''}</td>
              </tr>
            ))}
          </tbody></table>
        )}
      </div>

      {/* Registrar pago al referidor */}
      <div className={`${card} space-y-2`}>
        <h3 className="font-semibold text-content">Registrar pago al referidor</h3>
        <p className="text-xs text-content-3">Salda primero las comisiones más antiguas. Un abono parcial está bien: el resto queda pendiente.</p>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
          <input className={input} type="number" min={1} placeholder="Monto" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} />
          <input className={input} type="date" value={pay.paid_at} onChange={(e) => setPay({ ...pay, paid_at: e.target.value })} />
          <input className={input} placeholder="Forma (transferencia…)" value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })} />
          <input className={input} placeholder="Nota" value={pay.notes} onChange={(e) => setPay({ ...pay, notes: e.target.value })} />
        </div>
        <button className={btnPrimary} disabled={busy || !(Number(pay.amount) > 0)}
          onClick={() => window.confirm(`¿Registrar ${money(pay.amount)} pagados a ${r.name}?`) && run(async () => {
            await adminReferralsApi.payout({ referrer_id: r.id, amount: Number(pay.amount), paid_at: pay.paid_at, method: pay.method, notes: pay.notes });
            setPay({ amount: '', paid_at: hoy(), method: 'transferencia', notes: '' });
          })}>Registrar pago</button>
        {d.payouts.length > 0 && (
          <ul className="text-sm text-content-2 pt-1 space-y-0.5">
            {d.payouts.map((p) => <li key={p.id}>{fmtDia(String(p.paid_at).slice(0, 10))} · {money(p.amount, p.currency)}{p.method ? ` · ${p.method}` : ''}{Number(p.unallocated) > 0 ? ` · ${money(p.unallocated, p.currency)} a favor` : ''}{p.notes ? ` · ${p.notes}` : ''}</li>)}
          </ul>
        )}
      </div>

      {/* Movimientos */}
      <div className={`${card} space-y-2`}>
        <h3 className="font-semibold text-content">Movimientos</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-content-3 border-b border-line">
              <th className="py-1.5 pr-3">Mes</th><th className="pr-3">Negocio</th><th className="pr-3">Detalle</th><th className="pr-3 text-right">Monto</th><th className="pr-3">Estado</th><th className="pr-3">Grupo</th><th />
            </tr></thead>
            <tbody>
              {d.earnings.map((e) => (
                <tr key={e.id} className="border-b border-line/60">
                  <td className="py-1.5 pr-3 whitespace-nowrap capitalize">{fmtMes(e.period)}</td>
                  <td className="pr-3 text-content">{e.tenant_name}</td>
                  <td className="pr-3 text-content-2">{e.description}{e.payment_id ? ` · pago #${e.payment_id}` : ''}</td>
                  <td className="pr-3 text-right whitespace-nowrap tabular-nums">
                    {e.months_granted ? `${e.months_granted} meses` : money(e.amount, e.currency)}
                    {parcialTxt(e) ? <span className="block text-xs text-content-3">{parcialTxt(e)}</span> : null}
                  </td>
                  <td className="pr-3"><span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_EARN[e.status].chip}`}>{STATUS_EARN[e.status].label}</span></td>
                  <td className="pr-3 whitespace-nowrap">{cuentaGrupo(e) ? (
                    <span className="inline-flex items-center gap-1.5">
                      <GrupoChip e={e} otras={delNegocio(e.referral_id)} />
                      {e.grupo_anotado_at
                        ? <button className="text-xs text-content-3 hover:underline" disabled={busy} onClick={() => desmarcar(e)}>Desmarcar</button>
                        : <button className="text-xs text-accent hover:underline" disabled={busy} onClick={() => anotar(e)}>Marcar anotada</button>}
                    </span>
                  ) : <span className="text-xs text-content-3">—</span>}</td>
                  <td>{(e.status === 'pending' && Number(e.paid_amount) === 0) && (
                    <button className="text-xs text-red-600 hover:underline" disabled={busy}
                      onClick={() => { const why = window.prompt('¿Por qué se anula? (reembolso, error…)'); if (why) run(() => adminReferralsApi.voidEarning(e.id, why)); }}>Anular</button>
                  )}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Ganancia manual (pago fuera del sistema) */}
        <details className="pt-2">
          <summary className="text-sm text-accent cursor-pointer">Agregar a mano (pago fuera del sistema)</summary>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2">
            <select className={input} value={man.referral_id} onChange={(e) => setMan({ ...man, referral_id: e.target.value })}>
              <option value="">¿Qué negocio pagó?</option>
              {d.referrals.filter((x) => x.status !== 'void').map((x) => <option key={x.id} value={x.id}>{x.tenant_name}</option>)}
            </select>
            <input className={input} type="date" value={man.date} onChange={(e) => setMan({ ...man, date: e.target.value })} />
            {current?.kind === 'free_months'
              ? <input className={input} type="number" min={1} placeholder="Meses" value={man.months} onChange={(e) => setMan({ ...man, months: e.target.value })} />
              : <input className={input} type="number" min={0} placeholder="Comisión a entregar ($)" value={man.amount} onChange={(e) => setMan({ ...man, amount: e.target.value })} />}
            <input className={input} placeholder="Descripción" value={man.description} onChange={(e) => setMan({ ...man, description: e.target.value })} />
            {current?.kind === 'free_months' && (
              <label className="text-sm text-content-2 flex items-center gap-2 sm:col-span-2">
                <input type="checkbox" checked={man.apply} onChange={(e) => setMan({ ...man, apply: e.target.checked })} />
                Sumar los meses ahora a su suscripción{r.own_tenant_name ? ` (${r.own_tenant_name})` : ' (no tiene negocio ligado)'}
              </label>
            )}
          </div>
          <button className={`${btnPrimary} mt-2`} disabled={busy || !man.referral_id}
            onClick={() => run(async () => {
              await adminReferralsApi.manualEarning({ referral_id: Number(man.referral_id), date: man.date, description: man.description || 'Manual',
                ...(current?.kind === 'free_months'
                  ? { months_granted: Number(man.months) || null, apply_months: man.apply }
                  : { amount: Number(man.amount) || 0 }) });
              setMan({ referral_id: '', amount: '', months: '', apply: true, description: '', date: hoy() });
            })}>Agregar</button>
        </details>
      </div>
    </div>
  );
};

// ── Página ───────────────────────────────────────────────────────────────────
const AdminReferralsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const [rows, setRows] = useState<Referrer[]>([]);
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([adminReferralsApi.summary(), adminReferralsApi.tenants()]);
      setRows(s); setTenants(t); setError(null);
    } catch (e) { setError(errMsg(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const debe = useMemo(() => {
    const by: Record<string, number> = {};
    for (const r of rows) for (const t of r.totals || []) by[t.currency] = (by[t.currency] || 0) + t.pending;
    return by;
  }, [rows]);

  if (currentUser?.role !== 'admin') {
    return (
      <Layout>
        <div className="bg-red-50 dark:bg-red-500/10 p-4 rounded-md border border-red-200 flex gap-3">
          <LifeBuoy className="h-5 w-5 text-red-400" />
          <p className="text-sm text-red-700 dark:text-red-300">Solo los administradores pueden ver los referidos.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-5 animate-fade-in-up">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-xl font-bold text-content flex items-center gap-2"><Handshake className="h-6 w-6 text-accent" /> Referidos</h1>
            <p className="text-sm text-content-2 mt-0.5">Quién trajo a cada negocio, lo que ganó y lo que se le debe.</p>
          </div>
          <div className="flex gap-2">
            <button className={btn} onClick={load} disabled={loading}><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Actualizar</button>
            {selected == null && <button className={btnPrimary} onClick={() => setShowNew(true)}><Plus className="h-4 w-4" /> Nuevo referidor</button>}
          </div>
        </div>

        {error && <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 rounded-md p-3 text-sm text-red-700 dark:text-red-300">{error}</div>}

        {selected != null ? (
          <ReferrerDetailView id={selected} tenants={tenants} onBack={() => setSelected(null)} onChanged={load} />
        ) : (
          <>
            {showNew && <NewReferrerForm tenants={tenants} onCancel={() => setShowNew(false)} onDone={() => { setShowNew(false); load(); }} />}
            <div className={`${card} flex flex-wrap gap-6`}>
              <div><p className="text-xs text-content-3">Lealify debe a referidores</p>
                <p className="text-2xl font-bold text-content tabular-nums">{Object.keys(debe).length ? Object.entries(debe).map(([c, v]) => money(v, c)).join(' · ') : money(0)}</p></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {rows.map((r) => {
                const t = (r.totals || []).find((x) => x.currency === 'MXN') || (r.totals || [])[0];
                return (
                  <button key={r.id} onClick={() => setSelected(r.id)} className={`${card} text-left hover:bg-surface-2 ${r.active ? '' : 'opacity-60'}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-semibold text-content">{r.name} <span className="text-xs text-content-3">· {r.code}</span></p>
                        <p className="text-xs text-content-2">{schemeText(r.current_scheme)} · {r.referrals_count} {r.referrals_count === 1 ? 'negocio' : 'negocios'}{r.link_visitors ? ` · ${r.link_visitors} ${r.link_visitors === 1 ? 'visita' : 'visitas'} al enlace` : ''}{r.link_signups ? ` · ${r.link_signups} ${r.link_signups === 1 ? 'alta' : 'altas'} por enlace` : ''}</p>
                      </div>
                      {r.current_scheme && <span className="px-2 py-0.5 rounded-full text-xs bg-accent/15 text-accent whitespace-nowrap">{KIND_LABEL[r.current_scheme.kind]}</span>}
                    </div>
                    <div className="mt-3 flex gap-5 text-sm">
                      <div><p className="text-xs text-content-3">Pendiente</p><p className="font-bold tabular-nums text-content">{money(t?.pending, t?.currency)}</p></div>
                      <div><p className="text-xs text-content-3">Pagado</p><p className="tabular-nums text-content-2">{money(t?.paid, t?.currency)}</p></div>
                      {(t?.months_applied || 0) > 0 && <div><p className="text-xs text-content-3">Meses gratis</p><p className="tabular-nums text-content-2">{t?.months_applied}</p></div>}
                      {(t?.sin_anotar || 0) > 0 && <div><p className="text-xs text-amber-700 dark:text-amber-300">Falta anotar en el grupo</p><p className="font-semibold tabular-nums text-amber-700 dark:text-amber-300">{money(t?.sin_anotar, t?.currency)}</p></div>}
                    </div>
                  </button>
                );
              })}
              {!loading && rows.length === 0 && <p className="text-sm text-content-3">Todavía no hay referidores.</p>}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default AdminReferralsPage;
